<template>
  <iframe ref="frame" :src="frameUrl" />
</template>

<script>
import IGuideViewer from '@iguide/viewer';
import debounce from 'lodash/debounce';

const DEBOUNCE_DELAY = 500;

export default {
  props: {
    url: { type: String, required: true },
    isHost: { type: Boolean, required: true },
  },

  data() {
    return {
      viewer: null,
      isLandscape: null,
      hostIsLandscape: null,
      hostFloorplanMinimized: null,
      floorplanResizing: false,
    };
  },

  created() {
    window.addEventListener('resize', this.setIsLandscape);
  },

  mounted() {
    this.viewer = new IGuideViewer(this.$refs.frame);

    this.setIsLandscape();

    this.viewer.ready().then(() => {
      // emit the title of the tour
      const { title } = this.viewer.getMetaInfo();
      if (title) {
        this.$emit('title', title);
      }

      const emitMoveEventCb = (...args) => {
        this.viewer.removeEventListener('tour:move-end', emitMoveEndEventCb); // just in case
        this.$emit('event', { event: 'tour:move-start', args });
      };
      this.viewer.addEventListener('tour:move-start', emitMoveEventCb);

      // If the host leaves and comes back, reset everybody back to their location
      const emitMoveEndEventCb = (...args) => {
        this.viewer.removeEventListener('tour:move-end', emitMoveEndEventCb);
        this.$emit('event', { event: 'tour:move-start', args });
      };
      this.viewer.addEventListener('tour:move-end', emitMoveEndEventCb);

      const emitMouseEventCb = (...args) => {
        this.$emit('event', { event: 'tour:camera-move', args });
      };
      const debouncedEmitMouseEventCb = debounce(
        emitMouseEventCb,
        DEBOUNCE_DELAY,
        { maxWait: DEBOUNCE_DELAY },
      );
      this.viewer.addEventListener('tour:camera-move', debouncedEmitMouseEventCb);

      const emitFloorplanEventCb = (...args) => {
        this.$emit('event', { event: 'tour:floorplan-resize', args });
      };
      this.viewer.addEventListener('tour:floorplan-resize', emitFloorplanEventCb);

      this.viewer.addEventListener('tour:floorplan-resize-end', () => {
        this.floorplanResizing = false;
      });

      this.$root.$on('action', data => {
        if (!this.viewer) { return; }
        let prevHostIsLandscape;
        switch (data.event) {
        case 'tour:move-start':
          this.viewer.tour.move.apply(null, data.args);
          break;
        case 'tour:camera-move':
          this.viewer.tour.moveCamera.apply(null, data.args);
          break;
        case 'tour:floorplan-resize':
          this.hostFloorplanMinimized = data.args[0] === 'min';
          this.floorplanResizing = true;
          this.viewer.tour.floorplanResize(this.isFpMin() ? 'min' : 'max');
          break;
        case 'tour:landscape':
          prevHostIsLandscape = this.hostIsLandscape;
          this.hostIsLandscape = data.args[0];
          if (prevHostIsLandscape !== this.hostIsLandscape) {
            // XXX: Delay resizing as there is likely a floorplan-resize event coming next
            setTimeout(() => {
              if (!this.floorplanResizing) {
                if (this.cancelFpResize) {
                  this.cancelFpResize = false;
                } else {
                  this.viewer.tour.floorplanResize(this.isFpMin() ? 'min' : 'max');
                }
              }
            }, 300);
          }
          break;
        default:
          break;
        }
      });

      this.$root.$on('sync-start', msg => {
        if (!msg) { return; }

        this.hostIsLandscape = msg.landscape;
        this.hostFloorplanMinimized = msg.floorplan === 'min';
        this.viewer.tour.floorplanResize(this.isFpMin() ? 'min' : 'max');

        if (msg.last === 'move') {
          this.viewer.tour.move.apply(null, msg.move.args);
        } else if (msg.move && msg.camera) {
          const moveEndCb = () => {
            this.viewer.removeEventListener('tour:move-end', moveEndCb);
            this.viewer.tour.moveCamera.apply(null, msg.camera.args);
          };
          this.viewer.addEventListener('tour:move-end', moveEndCb);
          this.viewer.tour.move.apply(null, msg.move.args);
        } else {
          this.viewer.tour.moveCamera.apply(null, msg.camera.args);
        }
      });

      this.$emit('ready');
    });
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.setIsLandscape);
    this.viewer.disconnect();
    this.viewer = null;
  },

  methods: {
    setIsLandscape() {
      const prevIsLandscape = this.isLandscape;
      this.isLandscape = this.$refs.frame.clientWidth > this.$refs.frame.clientHeight;
      if (prevIsLandscape !== this.isLandscape) {
        this.$emit('landscape', this.isLandscape);

        if (this.isLandscape) {
          this.viewer.tour.floorplanResize(this.isFpMin() ? 'min' : 'max');
        } else if (!this.isHost) {
          const resizeEnd = () => {
            this.viewer.removeEventListener('tour:floorplan-resize-end', resizeEnd);
            this.viewer.tour.floorplanResize(this.isFpMin() ? 'min' : 'max');
          };
          this.viewer.addEventListener('tour:floorplan-resize-end', resizeEnd);
        }
      }
    },
    isFpMin() {
      // XXX: If isHost is true, hostIsLandscape and hostFloorplanMinimized will be null which
      // is fine because it will return the correct isFpMin value for the host anyways.
      return this.hostIsLandscape === this.isLandscape ?
        this.hostFloorplanMinimized : !this.isLandscape;
    },
  },

  computed: {
    frameUrl() {
      // Ensure URL has `api=1` to enable API support
      const u = new URL(this.url);
      const params = new URLSearchParams(u.search);
      params.set('api', '1');
      params.set('page', 'tour');
      params.set('unbranded', '1');
      params.set('autostart', '1');
      params.set('noinitanimation', '1');
      params.set('nocontrols', '1');
      params.set('nomenu', '1');
      params.set('noinithelp', '1');
      u.search = params.toString();
      return u.toString();
    },
  },
};
</script>

<style>
iframe {
  width: 100%;
  border: none;
}
</style>
